import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { getBlogPost } from "@/lib/blog.functions";
import { autoExcerpt, plainText, readingMinutes } from "@/lib/blog-shared";
import { BlogBody } from "@/components/BlogBody";
import { BlogReactions } from "@/components/BlogReactions";
import { Ss01BlogCards } from "@/components/Ss01BlogCards";
import { AffiliateDisclosure } from "@/components/AffiliateDisclosure";
import { breadcrumbSchema } from "@/lib/schema";
import ss01KeyVisual from "@/assets/ss01/slide.webp.asset.json";
import { isOctoberTcgPost, octoberTcgFaq } from "@/lib/blog-october-tcg";
import { faqSchema } from "@/components/PageFaq";

export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ params }) => {
    const post = await getBlogPost({ data: { slug: params.slug } });
    if (!post) throw notFound();
    return { post };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Article unavailable — Pal Stars" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const { post } = loaderData;
    const url = `https://palstars.com/blog/${post.slug}`;
    const desc = autoExcerpt(post);
    const isSs01Reveal = post.slug === "palworld-tcg-sleeve-card-set-vol-1-cards-revealed";
    const image = post.cover_image && post.cover_image.startsWith("https://")
      ? post.cover_image
      : post.cover_image && post.cover_image.startsWith("/")
        ? `https://palstars.com${post.cover_image}`
        : isSs01Reveal
          ? `https://palstars.com${ss01KeyVisual.url}`
          : null;
    return {
      meta: [
        { title: post.title.slice(0, 60) },
        { name: "description", content: desc },
        { property: "og:title", content: post.title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        ...(image
          ? [
              { property: "og:image", content: image },
              { name: "twitter:image", content: image },
            ]
          : []),
        { name: "twitter:card", content: "summary_large_image" },
        { name: "robots", content: "index, follow, max-snippet:-1, max-image-preview:large" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        ...(isOctoberTcgPost(post.slug) ? [faqSchema(octoberTcgFaq(post.slug, post.body), url)] : []),
        breadcrumbSchema([
          ["Home", "/"],
          ["Blog", "/blog"],
          [post.title, `/blog/${params.slug}`],
        ]),
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": ["BlogPosting", "NewsArticle"],
            "@id": `${url}#article`,
            headline: post.title,
            description: desc,
            url,
            inLanguage: "en",
            isAccessibleForFree: true,
            commentCount: 0,
            author: { "@type": "Organization", name: post.author || "Pal Stars", url: "https://palstars.com" },
            publisher: { "@type": "Organization", name: "Pal Stars", url: "https://palstars.com" },
            mainEntityOfPage: { "@type": "WebPage", "@id": url },
            articleSection: post.tag,
            speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "h2", ".aeo-answer"] },
            wordCount: plainText(post.body).split(/\s+/).filter(Boolean).length,
            ...(image ? { image } : {}),
          }),
        },
      ],
    };
  },
  component: BlogPostPage,
  notFoundComponent: PostNotFound,
});

function BlogPostPage() {
  const { post } = Route.useLoaderData();
  const hasAffiliate = /ebay\.(com|co\.uk)/i.test(post.body);
  const isSs01Reveal = post.slug === "palworld-tcg-sleeve-card-set-vol-1-cards-revealed";
  const isMarketCorrection = post.slug === "palworld-tcg-market-correction-2026";
    const isMobileGarena = post.slug === "palworld-mobile-garena-korea-beta-gameplay";

  return (
    <article className="mx-auto max-w-3xl px-4 sm:px-6 py-10 space-y-6">
      <header>
        <p className="text-[11px] uppercase tracking-widest text-primary">
          {post.tag} · {readingMinutes(post.body)} min read
        </p>
        <h1 className="mt-2 text-3xl font-extrabold sm:text-4xl">{post.title}</h1>
        <p className="mt-2 text-sm text-muted-foreground">By {post.author || "Pal Stars"}</p>
      </header>

      {post.cover_image ? (
        <img
          src={post.cover_image}
          alt={post.cover_alt ?? post.title}
          width={1280}
          height={720}
          loading={isSs01Reveal || isMarketCorrection || isMobileGarena || isOctoberTcgPost(post.slug) ? "lazy" : "eager"}
          decoding="async"
          className="aspect-[16/9] w-full rounded-2xl border border-border/60 object-cover"
        />
      ) : null}

      {hasAffiliate ? <AffiliateDisclosure compact /> : null}

      <BlogBody body={post.body} hideSs01CardImages={isSs01Reveal} />

      {isSs01Reveal ? <Ss01BlogCards /> : null}

      {isSs01Reveal ? (
        <p className="text-sm text-muted-foreground">
          Compare all card effects, deck uses and parallel details in the{" "}
          <Link
            to="/guides/$slug"
            params={{ slug: "palworld-tcg-sleeve-card-set-vol-1" }}
            className="font-semibold text-primary hover:underline"
          >
            Palworld Sleeve &amp; Card Set Vol.1 guide
          </Link>
          .
        </p>
      ) : null}

      <BlogReactions postId={post.id} title={post.title} />

      <section className="glass rounded-2xl p-5">
        <h2 className="text-xl font-bold">More from Pal Stars</h2>
        <ul className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
          <li>
            <Link to="/blog" className="font-semibold text-primary hover:underline">
              All Palworld blog articles →
            </Link>
          </li>
          <li>
            <Link to="/prices" className="font-semibold text-primary hover:underline">
              Palworld TCG price chart →
            </Link>
          </li>
          <li>
            <Link to="/guides" className="font-semibold text-primary hover:underline">
              Palworld guides library →
            </Link>
          </li>
          <li>
            <Link to="/cards" className="font-semibold text-primary hover:underline">
              Palworld card database →
            </Link>
          </li>
        </ul>
      </section>
    </article>
  );
}

function PostNotFound() {
  return (
    <div className="mx-auto max-w-3xl px-4 py-16 text-center">
      <h1 className="text-3xl font-bold">Article not found</h1>
      <p className="mt-3 text-muted-foreground">
        That post has moved or was never published.{" "}
        <Link to="/blog" className="font-semibold text-primary hover:underline">
          Browse the Palworld blog →
        </Link>
      </p>
    </div>
  );
}
